import * as THREE from 'three';
import { buildBVH } from '../utils/bvh';

// momentSight — real sight lines for a moment's still (city/moment, 2026-11-01).
//
// The shot planner (sceneShots) checks its lines against the road map, which
// knows buildings and nothing else: the first real still had a lamp post dead
// centre between the two people. A still is planned ONCE, so it can afford rays
// against the scene itself: from the camera to each person's eyes and chest,
// stopping just short of them, and anything solid in between refuses the spot.
//
// …AND THE SPACE BETWEEN THEM. The lamp post in that first still covered
// neither face — it stood in the gap between the two, dead centre in the frame.
// So with `test.band = { a, b }` (the pair, as the planner holds them), the
// test for `a` also sends rays to points along the line from a to b, between
// head and chest: a two-shot needs the whole span of them clear.
//
// The scene gets bounding-volume trees first (utils/bvh — the same trees the
// room floor uses), so each ray is cheap; built once, when the city is in.
//
// "Solid" leaves out what a camera sees through: anything hidden (or under
// something hidden), sprites, points and lines (rain, labels, markers),
// see-through or depth-less materials (glass, glows, decals) — and bodies.
const EYE_UP = 0.03;      // eyes, from the planner's `head` (about the chin)
const CHEST_DOWN = 0.22;  // and the chest
const SHORT = 0.2;        // stop this short of them: their own face and chest are not in the way
const ACROSS = [0.25, 0.5, 0.75];   // where the span between them is checked

function solid(obj) {
  // Bodies are left to the planner's own checks (a head in the way, NEAR_SAFE):
  // a skinned mesh's raw geometry is its bind pose, often at the rig's own scale,
  // and would stand in the street as a phantom wall.
  if (!obj?.isMesh || obj.isSkinnedMesh || obj.userData?.noOcclude) return false;
  for (let x = obj; x; x = x.parent) if (x.visible === false) return false;
  const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
  return mats.some((m) => m && m.visible !== false && !(m.transparent && ((m.opacity ?? 1) < 0.35 || m.depthWrite === false)));
}

/** A sight test for sceneShots.setSightTest: (camera [x,y,z], person { x, z, head }) → clear? */
export function sightTestFor(scene) {
  const t0 = performance.now();
  const { built, tris } = buildBVH(scene);
  console.log(`[moment] sight lines: ${built} tree(s) over ${tris} triangles in ${Math.round(performance.now() - t0)} ms`);
  const ray = new THREE.Raycaster();
  const o = new THREE.Vector3();
  const d = new THREE.Vector3();
  let rays = 0;
  const clearTo = (c, x, y, z, short) => {
    o.set(c[0], c[1], c[2]);
    d.set(x - c[0], y - c[1], z - c[2]);
    const L = d.length();
    if (L <= short + 0.05) return false;
    d.divideScalar(L);
    ray.set(o, d);
    ray.near = 0.05;
    ray.far = L - short;
    rays += 1;
    const hits = ray.intersectObject(scene, true);
    for (let i = 0; i < hits.length; i++) if (solid(hits[i].object)) return false;
    return true;
  };
  const test = (c, p) => {
    for (const y of [p.head + EYE_UP, p.head - CHEST_DOWN]) if (!clearTo(c, p.x, y, p.z, SHORT)) return false;
    const band = test.band;
    if (band && p === band.a && band.b) {
      const q = band.b;
      const y = (p.head + q.head) / 2 - 0.1;
      for (const t of ACROSS) if (!clearTo(c, p.x + (q.x - p.x) * t, y, p.z + (q.z - p.z) * t, 0.05)) return false;
    }
    return true;
  };
  test.band = null;
  test.count = () => rays;
  return test;
}
